import type { ReactNode } from 'react';

export type StatTileTone = 'default' | 'pos' | 'neg' | 'warning';

export interface StatTileProps {
  readonly label: ReactNode;
  readonly value: ReactNode;
  readonly sub?: ReactNode;
  readonly tone?: StatTileTone;
  readonly className?: string;
}

const TONE_CLASS: Record<StatTileTone, string> = {
  default: 'text-text-primary',
  pos: 'text-pos',
  neg: 'text-neg',
  warning: 'text-warning',
};

/**
 * Big-number tile. 2×2 grids of these fill MinerDetailSheet tabs.
 *
 * Visual: surface-2 bg, 10px radius, uppercase 9px label + 20px mono
 * value + optional 10px sub-line in text-tertiary.
 *
 * Reference: docs/design_handoff/.../screens-settings.jsx StatTile.
 */
export function StatTile({
  label,
  value,
  sub,
  tone = 'default',
  className = '',
}: StatTileProps): ReactNode {
  return (
    <div
      className={[
        'rounded-[10px] bg-surface-2 px-3 py-3',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="text-[9px] font-bold uppercase tracking-[1.2px] text-text-tertiary">
        {label}
      </div>
      <div
        data-stat-value
        className={[
          'mt-1 font-mono text-[20px] font-medium leading-[1.05] tracking-[-0.3px] tabular-nums',
          TONE_CLASS[tone],
        ].join(' ')}
      >
        {value}
      </div>
      {sub !== undefined && (
        <div className="mt-1 text-[10px] text-text-tertiary tracking-[0.1px]">
          {sub}
        </div>
      )}
    </div>
  );
}
